import type { StringFileResponse } from 'models/FileResponse';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import type { IHttpClient } from 'utils/http/httpClient.model';

import type { IFetchColumnDefinitionEpicApi, ISearchResponse, SearchResponse } from './common';

export type BlotterItemType = 'Quote' | 'Trade' | 'Product' | 'PriceNegotiation' | 'Order';

export type OrderType = 'Live' | 'Limit order' | 'Order on close';

export interface IUnifiedOrderBlotterEntry {
  accruedInterest?: number;
  blotterItemType: BlotterItemType;
  dealId: string;
  orderId: string;
  currency: string;
  isin: string;
  nominal?: number;
  maturityDate: string;
  quotationType: string;
  priceType: string;
  price: number;
  fileCode?: string;
  productIdSource: string;
  productIdType: string;
  productIdValue: string;
  settlementQuantity?: number;
  settlementCashAmount?: number;
  executionTime: string;
  creationTime: string;
  comment: string;
  settlementType: string;
  settlementDate: Date;
  valuationDate: Date;
  salesBeneficiary: string;
  salesBeneficiaryEmail: string;
  platform: string;
  tradeSide: string;
  salesCounterpartName: string;
  orderStatus: string;
  orderType: OrderType;
  cobDate: Date;
  tradeDate: Date;
  tradingPrice: number;
  salesMargin: number;
  requester: string;
  adjustedSalesMargin?: number;
  adjustedTradingPrice?: number;
  adjustedUpfrontFee?: number;
  upfront?: number;
  settlementUnit?: string;

  productReference?: string;
  timeBeforeBooking?: string;

  clientLimit?: number;
  status: string;
}

export interface ISecondaryOrderBlotterFetchCsvApi {
  fetchCSV(): Observable<StringFileResponse>;
}

export interface ISecondaryOrderBlotterFetchItemsApi {
  search(): Observable<SearchResponse>;
}

export interface ISecondaryOrderBlotterApi
  extends
    ISecondaryOrderBlotterFetchItemsApi,
    IFetchColumnDefinitionEpicApi<IUnifiedOrderBlotterEntry>,
    ISecondaryOrderBlotterFetchCsvApi {}

export function secondaryOrderBlotterApiConnector(http: IHttpClient): ISecondaryOrderBlotterApi {
  return {
    search() {
      return http
        .getJSON<ISearchResponse<IUnifiedOrderBlotterEntry>>({
          url: 'api/v1/secondary/blotter/orders',
        })
        .pipe(map(({ payload }) => payload));
    },
    fetchColumnDefinition() {
      return http
        .getJSON<{
          data: { columnDefinitions: IBlotterColumnDefinition<IUnifiedOrderBlotterEntry>[] };
          isSuccess: boolean;
        }>({
          url: 'api/v1/blotter/column-definitions/orders',
        })
        .pipe(map(({ payload }) => payload.data?.columnDefinitions ?? []));
    },
    fetchCSV() {
      return http
        .getRAW({
          url: 'api/v1/secondary/blotter/orders/csv',
        })
        .pipe(
          map(response => ({
            data: response.payload,
            filename: response.attachmentName,
          })),
        );
    },
  };
}
